import { Box } from "@mui/material"
import { BigNumber } from "ethers"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import Modal from "src/components/Modal"
import { enqueueToast } from "src/components/Toastify"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { XFER } from "src/constants"
import boostPools from "src/constants/boostPools"
import { useActiveWeb3React } from "src/hooks"
import { useAction } from "src/hooks/useAction"
import {
  actions,
  fetchFerroBoostPoolDataAsync,
  fetchFerroBoostUserDataAsync,
} from "src/state/xFer"
import { formatNumber, getRawAmount } from "src/utils/parseStringToBigNumber"
import { formatLockPeriod } from "../../helpers"
import useFerroBoostHandlers from "../../hooks/useFerroBoostHandlers"
import ConfirmStep from "./ConfirmStep"
import SelectPeriodStep from "./SelectPeriodStep"

interface UpgradeModalProps {
  stakeIds: number[]
  poolId: number
  amount: BigNumber
  lastUnlockTime: number
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  poolId,
  stakeIds,
  amount,
  lastUnlockTime,
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [selectedPool, setSelectePool] = useState(0)

  const goNext = useCallback(() => {
    setStep((state) => state + 1)
  }, [])

  const { account } = useActiveWeb3React()
  const dispatch = useDispatch()
  const { onBatchUpgrade } = useFerroBoostHandlers()
  const closeUpgradeModal = useAction(actions.setOpenedUpgradeModal, null)

  const selectedPoolInfo = boostPools.find((pool) => pool.pid === selectedPool)

  const summary: TransactionSummaryData = [
    {
      label: t("Upgrade"),
      customElement: (
        <Box display="flex" alignItems="center">
          <Box mr={1}>
            {formatLockPeriod(selectedPoolInfo?.lockPeriod || 0)}
          </Box>
          <img src={selectedPoolInfo?.imgSrc} width={16} height={16} />
        </Box>
      ),
    },
    {
      label: t("Deposit"),
      amount: formatNumber(getRawAmount(amount, XFER.decimals), 2, 6),
      token: XFER,
    },
  ]

  const handleConfirmClick = async () => {
    try {
      if (selectedPoolInfo) {
        await onBatchUpgrade(
          stakeIds,
          stakeIds.map(() => selectedPool),
          summary,
        )
      }
      if (account) {
        dispatch(fetchFerroBoostUserDataAsync(account))
        dispatch(fetchFerroBoostPoolDataAsync())
      }
    } catch (e: any) {
      console.error(e)
      enqueueToast(
        "error",
        t("Your xFER amount has been reverted"),
        t("Vault upgrade failed"),
      )
    } finally {
      closeUpgradeModal()
    }
  }

  return (
    <Modal
      open
      onClose={closeUpgradeModal}
      title={step === 0 ? t("Upgrade Lock Period") : t("Confirm Upgrade")}
    >
      {step === 0 && (
        <SelectPeriodStep
          currentPool={poolId}
          amount={amount}
          selectedPool={selectedPool}
          onSelect={setSelectePool}
          onConfirm={goNext}
          lastUnlockTime={lastUnlockTime}
        />
      )}
      {step === 1 && (
        <ConfirmStep
          onConfirm={handleConfirmClick}
          amount={amount}
          currentPool={poolId}
          selectedPool={selectedPool}
          lastUnlockTime={lastUnlockTime}
        />
      )}
    </Modal>
  )
}

export default UpgradeModal
